/**
 * Drill-down modal state for Supplier Performance: which list to open for a
 * cell, the request behind it, paging and the full-population export.
 *
 * Split out of useSupplierPerformance (which was past the 500-line limit) so
 * the page hook owns the ranked list and this one owns the modal. One
 * `requestFor` builds the request for a modal type; opening, paging and
 * exporting all go through it, so the export can never list a different
 * population from the one on screen.
 */

import { useRef, useState } from 'react';
import {
  purchasingAPI,
  qualityAPI,
  AxiosResponse,
  QueryParams,
  SupplierSelfPerformanceRow,
} from '../../lib/api';
import { SupplierPerformanceFilterValues } from '../../components/suppliers/SupplierPerformanceFilters';
import { useFormatters } from '../../contexts/PreferencesContext';
import { getErrorMessage } from '../../hooks/useFetchData';
import { toast } from 'sonner';
import { ModalType, ModalContext, POLineMetric, DrillSite } from './types';
import { MODAL_PAGE_SIZE } from './constants';
import { buildSelfDrilldownParams } from './selfPerformance';
import { AnyRow, DrilldownFormatters } from './drilldown/columns';
import { columnsFor, MODAL_TYPE_LABEL } from './drilldown/columnsFor';
import { drilldownFileName, exportDrilldownRows, fetchEveryPage } from './drilldown/exportDrilldown';

type OpenModalType = Exclude<ModalType, ''>;
type Params = Record<string, string | number | boolean>;
type ListResponse = { count: number; results: unknown[]; next?: string | null };
type Fetcher = (params: QueryParams) => Promise<AxiosResponse<ListResponse>>;

/** Largest page the list endpoints serve (StandardResultsSetPagination). */
const EXPORT_PAGE_SIZE = 1000;

/** Cells whose figure is money, not a count or a quantity. */
const MONEY_CELLS = new Set(['Total Value', 'Reject Value']);

/**
 * Cell label → the PO-line list's `performance_metric`. Each cell opens
 * exactly the population it counted (the same engine definition the profile
 * serializer's `po_line_count` / `otif_count` / `arrears_count` / `qty_diff`
 * use) instead of the supplier's whole line list.
 */
const POLINE_METRICS: Record<string, POLineMetric> = {
  'PO Lines': 'po_lines',
  OTIF: 'otif',
  Arrears: 'arrears',
  'Qty Diff': 'qty_diff',
};

interface UseDrilldownModalArgs {
  isSupplier: boolean;
  selfRows: SupplierSelfPerformanceRow[];
  filters: SupplierPerformanceFilterValues;
  getViewQueryParams: () => Record<string, number | undefined>;
  currency: string;
}

export function useDrilldownModal({
  isSupplier,
  selfRows,
  filters,
  getViewQueryParams,
  currency,
}: UseDrilldownModalArgs) {
  const { formatDate, formatDateTime, formatCurrency } = useFormatters();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalSubtitle, setModalSubtitle] = useState('');
  const [modalData, setModalData] = useState<object[]>([]);
  const [modalType, setModalType] = useState<ModalType>('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalPage, setModalPage] = useState(1);
  const [modalTotalCount, setModalTotalCount] = useState(0);
  const [modalContext, setModalContext] = useState<ModalContext | null>(null);
  const [modalExporting, setModalExporting] = useState(false);
  // Monotonic request id: a slow response for a modal the user has already
  // left (closed, re-opened on another cell, paged on) must not overwrite it.
  const requestSeqRef = useRef(0);

  const modalTotalPages = Math.ceil(modalTotalCount / MODAL_PAGE_SIZE);

  // Customer review 11 Sep (item 5c): the subtitle repeats the cell's
  // figure — money with its currency and thousands separators ("£3,408,854.43",
  // not "3408854.43"), quantities to at most 2 dp.
  const cellFigure = (filterType: string | undefined, value: number): string =>
    filterType && MONEY_CELLS.has(filterType)
      ? formatCurrency(value, currency, 2)
      : value.toLocaleString('en-GB', { maximumFractionDigits: 2 });

  // Q8: a sister site's row (GROUP view) is readable but the list endpoints
  // only serve the user's own site, so its drill-down would show nothing —
  // say so instead of opening an empty modal (R-135 tracks the widening).
  const canDrill = (rowKey: number): boolean => {
    if (!isSupplier) return true;
    const row = selfRows.find((r) => r.self_key === rowKey);
    if (row && row.is_own_site === false) {
      toast.info(`Drill-downs for ${row.supplier_site_name} open from that site's own login.`);
      return false;
    }
    return true;
  };

  // Build common filter params for drill-down API calls.
  //
  // CR-6004 round 4: inject the active view-context scope (group / company /
  // site) the same way fetchData does, so the modal's API call agrees with
  // the SP table's column query. Pull from getViewQueryParams first; the
  // explicit page-level filter overrides if present.
  //
  // SI-16: on the supplier-side page the row key is the row's client key
  // (`self_key`), which resolves to a (supplier site × customer) pair; each
  // list endpoint names the customer differently (kind).
  //
  // Customer review 11 Sep (stream I, from B): a per-site breakdown row passes
  // its `site`. Its figures were computed with the view scope REPLACED by that
  // one site (useSupplierPerformance.buildSiteRowParams), so its drill-down
  // does the same — `site` only, no group/company — and lists exactly the
  // records behind that row's cell. The backend intersects `site` with the
  // viewer's grant (security.access.resolve_scope_company_ids), so a site
  // the user does not hold comes back empty, never wider.
  const buildDrilldownParams = (rowKey: number, kind: OpenModalType, site?: DrillSite): Params => {
    if (isSupplier) {
      const row = selfRows.find((r) => r.self_key === rowKey);
      if (!row) return { supplier: 0 };
      return buildSelfDrilldownParams(kind, row, filters);
    }
    const params: Params = { supplier: rowKey };
    // Q7 (both viewers): the NCR list returns the NCRs the supplier is judged
    // on — no drafts, no internal — so the modal count equals the cell.
    if (kind === 'ncr') params['judged'] = true;
    if (site) {
      params['site'] = site.id;
    } else {
      const viewParams = getViewQueryParams();
      if (viewParams['group']) params['group'] = viewParams['group'];
      if (viewParams['company']) params['company'] = viewParams['company'];
      if (viewParams['site']) params['site'] = viewParams['site'];
      if (filters.site && filters.site !== 'all') params['site'] = filters.site;
    }
    if (filters.dateFrom) params['date_from'] = filters.dateFrom;
    if (filters.dateTo) params['date_to'] = filters.dateTo;
    if (filters.supplierGroup && filters.supplierGroup !== 'all') {
      params['company__group'] = filters.supplierGroup;
    }
    // Customer review 11 Sep: the Warehouse filter is NOT sent here. The
    // ranked list's request never carries it (buildProfileParams), so no cell
    // is narrowed by warehouse — forwarding it made the modal list fewer rows
    // than the figure that opened it. Restore it together with the cells.
    return params;
  };

  /** The request one modal issues — shared by open, page change and export. */
  const requestFor = (type: OpenModalType, ctx: ModalContext): { params: Params; fetch: Fetcher } => {
    const params = buildDrilldownParams(ctx.supplierId, type, ctx.site);
    switch (type) {
      case 'po':
        return { params, fetch: purchasingAPI.getOrders };
      case 'ncr':
        // METRIC-DEFINITIONS wave 3c+3e, FIX-17: this modal is titled with the
        // cell's own count ("NCRs: 14"), so it must open the population that
        // cell counted — non-deleted, no drafts, no internal, supplier flag
        // applied (W3-T21's countable set).
        params['countable'] = true;
        // Customer review 11 Sep (items 6, 7, 9): the drill's own row —
        // responder, time taken, customer NCR number, Reject Value money.
        params['performance_columns'] = true;
        // CR-6193: Filter by proposed_rejection_type when viewing Returns
        if (ctx.filterType === 'Returns') params['proposed_rejection_type'] = 1; // 1 = Return to Supplier
        return { params, fetch: qualityAPI.getNCRs };
      case 'grn':
        // CR-6134: OTD status filter for Ontime/Late/Early drilldowns
        if (ctx.otdStatus) params['on_time_status'] = ctx.otdStatus;
        params['all_suppliers'] = true;
        // Customer review 11 Sep (items 5, 8, 15): the slim drill row
        // (no per-row appeal lookups, so a full export stays fast) with the
        // frozen receipt value the Total Value cell sums.
        params['performance_columns'] = true;
        return { params, fetch: purchasingAPI.getGRNs };
      case 'pol':
        // The cells never apply a buyer's "my suppliers" code scope, so the
        // list behind them must not either (the GRN drill already lifts it).
        params['all_suppliers'] = true;
        if (ctx.additionalFilters?.status) params['status'] = ctx.additionalFilters.status;
        if (ctx.additionalFilters?.on_hold !== undefined) params['on_hold'] = ctx.additionalFilters.on_hold;
        if (ctx.metric) {
          // The cell's population. Its window is the page's date filter on
          // the PROMISE date (window_from/to), and its arrears stock is not
          // windowed at all — so the list's own date_from/date_to (which
          // narrow on delivery_date) must not travel with it. Qty Diff is
          // never windowed (SupplierProfileSerializer.get_qty_diff).
          delete params['date_from'];
          delete params['date_to'];
          params['performance_metric'] = ctx.metric;
          if (ctx.metric !== 'qty_diff') {
            if (filters.dateFrom) params['window_from'] = filters.dateFrom;
            if (filters.dateTo) params['window_to'] = filters.dateTo;
            // W3-T9: Historical/Live decides the arrears term, as on the cell.
            if (filters.dataMode === 'live') params['data_mode'] = 'live';
          }
        }
        return { params, fetch: purchasingAPI.getLines };
    }
  };

  const openModal = async (
    type: OpenModalType,
    ctx: ModalContext,
    page: number,
    title: string,
    subtitle: string,
  ): Promise<ListResponse | null> => {
    const seq = ++requestSeqRef.current;
    setModalTitle(title);
    setModalSubtitle(subtitle);
    setModalType(type);
    setModalOpen(true);
    setModalLoading(true);
    setModalError(null);
    setModalPage(page);
    setModalContext(ctx);
    try {
      const { params, fetch } = requestFor(type, ctx);
      const response = await fetch({ ...params, page, page_size: MODAL_PAGE_SIZE });
      if (seq !== requestSeqRef.current) return null;
      setModalData((response.data.results || []) as object[]);
      setModalTotalCount(response.data.count || 0);
      return response.data;
    } catch (err: unknown) {
      if (seq !== requestSeqRef.current) return null;
      console.error(`Error loading ${MODAL_TYPE_LABEL[type]} drill-down:`, err);
      setModalData([]);
      setModalTotalCount(0);
      const status = (err as { response?: { status?: number } })?.response?.status;
      setModalError(
        status === 403
          ? 'You do not have permission to view these records'
          : getErrorMessage(err, 'Failed to load records'),
      );
      return null;
    } finally {
      if (seq === requestSeqRef.current) setModalLoading(false);
    }
  };

  // The name a drill is filed under — title, and the export's file name.
  // A site row's drill names its site too ("a supplier · GARDNER
  // AEROSPACE - DERBY"), so the modal says which site's records it lists.
  const scopedName = (supplierName: string, site?: DrillSite) =>
    site ? `${supplierName} · ${site.name}` : supplierName;

  // PO Drilldown
  const openPOModal = async (supplierId: number, supplierName: string) => {
    if (!canDrill(supplierId)) return;
    await openModal('po', { supplierId, supplierName }, 1,
      `Purchase Orders - ${supplierName}`, 'Purchase order details');
  };

  // NCR Drilldown
  const openNCRModal = async (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    site?: DrillSite,
  ) => {
    if (!canDrill(supplierId)) return;
    const filterLabel = filterType ? ` (${filterType})` : '';
    const name = scopedName(supplierName, site);
    const data = await openModal('ncr', { supplierId, supplierName: name, filterType, value, site }, 1,
      `Non-Conformance Reports - ${name}${filterLabel}`,
      value !== undefined ? `${filterType || 'Parts rejected'}: ${cellFigure(filterType, value)}` : 'Non-conformance report details');
    // SI-16 Q7: a supplier cannot open NCRs the customer has not assigned to
    // it (liability undetermined and nothing to action), but they still
    // count — say so instead of looking short.
    if (data && isSupplier && (filterType === 'NCRs' || filterType === 'Returns') && value !== undefined && (data.count || 0) < value) {
      setModalSubtitle(`${filterType || 'NCRs'}: ${cellFigure(filterType, value)} · ${(data.count || 0).toLocaleString()} visible to you`);
    }
  };

  // GRN Drilldown
  // CR-6134: Added otdStatus parameter to filter GRNs by on-time delivery status
  const openGRNModal = async (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    otdStatus?: 'on_time' | 'late' | 'early',
    site?: DrillSite,
  ) => {
    if (!canDrill(supplierId)) return;
    const filterLabel = filterType ? ` (${filterType})` : '';
    const name = scopedName(supplierName, site);
    await openModal('grn', { supplierId, supplierName: name, filterType, value, otdStatus, site }, 1,
      `Goods Receipt Notes - ${name}${filterLabel}`,
      value !== undefined ? `${filterType || 'Parts delivered'}: ${cellFigure(filterType, value)}` : 'Goods receipt details');
  };

  // PO Lines Drilldown
  const openPOLineModal = async (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    additionalFilters?: { status?: string; on_hold?: boolean },
    site?: DrillSite,
  ) => {
    if (!canDrill(supplierId)) return;
    const filterLabel = filterType ? ` (${filterType})` : '';
    const metric = filterType ? POLINE_METRICS[filterType] : undefined;
    const name = scopedName(supplierName, site);
    const data = await openModal('pol', { supplierId, supplierName: name, filterType, value, additionalFilters, metric, site }, 1,
      `Purchase Order Lines - ${name}${filterLabel}`,
      value !== undefined ? `${filterType}: ${cellFigure(filterType, value)}` : 'Purchase order line details');
    // Qty Diff is a quantity, not a row count: say how many lines carry it.
    if (data && metric === 'qty_diff' && value !== undefined) {
      const lines = data.count || 0;
      setModalSubtitle(`Qty Diff: ${cellFigure(filterType, value)} across ${lines.toLocaleString()} line${lines === 1 ? '' : 's'}`);
    }
  };

  // Handle modal page change — the same request, next page; title and
  // subtitle stay as the opener set them.
  const handleModalPageChange = (newPage: number) => {
    if (!modalContext || modalLoading || !modalType) return;
    openModal(modalType, modalContext, newPage, modalTitle, modalSubtitle);
  };

  // Export the FULL filtered population — every page of the same request the
  // modal is showing — with the table's own columns.
  const handleModalExport = async () => {
    if (!modalContext || !modalType || modalExporting) return;
    const type = modalType;
    const ctx = modalContext;
    setModalExporting(true);
    try {
      const { params, fetch } = requestFor(type, ctx);
      // Every page of the population, not the 20 on screen (no fixed page
      // cap: fetchAllPages' 50-page default would cut a 55k-GRN export short).
      const rows = await fetchEveryPage<AnyRow>(
        async (page) => {
          const response = await fetch({ ...params, page, page_size: EXPORT_PAGE_SIZE });
          return { count: response.data.count || 0, results: (response.data.results || []) as AnyRow[] };
        },
        EXPORT_PAGE_SIZE,
      );
      if (rows.length === 0) {
        toast.info('No records to export');
        return;
      }
      const formatters: DrilldownFormatters = {
        formatDate,
        formatDateTime,
        money: (value, code) => formatCurrency(value, code, 2),
        currency,
      };
      const metricLabel = ctx.filterType ?? MODAL_TYPE_LABEL[type];
      exportDrilldownRows(
        rows,
        columnsFor(type, formatters, { filterType: ctx.filterType, metric: ctx.metric, isSupplier }),
        drilldownFileName(ctx.supplierName, metricLabel),
        metricLabel,
      );
      toast.success(`Exported ${rows.length.toLocaleString()} record${rows.length === 1 ? '' : 's'}`);
    } catch (err: unknown) {
      console.error('Drill-down export failed:', err);
      toast.error('Export failed — please try again');
    } finally {
      setModalExporting(false);
    }
  };

  return {
    modalOpen,
    modalTitle,
    modalSubtitle,
    modalData,
    modalType,
    modalLoading,
    modalError,
    modalPage,
    modalTotalCount,
    modalTotalPages,
    modalContext,
    modalExporting,
    setModalOpen,
    handleModalPageChange,
    handleModalExport,
    openPOModal,
    openNCRModal,
    openGRNModal,
    openPOLineModal,
  };
}
